// Web Notifications API helper for browser/OS-level DM alerts when the
// app tab is not focused. No-op on native (FCM push covers that) and on
// unsupported browsers. Sound respects the user's in-app sound setting
// via the `silent` flag on the Notification (OS plays the sound).

import { isNativePlatform } from "@/lib/platform";
import { getSoundEnabled } from "@/lib/soundSettings";

export type WebNotifPermission = "granted" | "denied" | "default" | "unsupported";

const SOFT_PROMPT_KEY = "lr.webNotif.softPrompted";

export function isWebNotificationSupported(): boolean {
  if (typeof window === "undefined") return false;
  if (isNativePlatform()) return false;
  return "Notification" in window;
}

export function getWebNotificationPermission(): WebNotifPermission {
  if (!isWebNotificationSupported()) return "unsupported";
  return Notification.permission as WebNotifPermission;
}

export async function requestWebNotificationPermission(): Promise<WebNotifPermission> {
  if (!isWebNotificationSupported()) return "unsupported";
  if (Notification.permission !== "default") {
    return Notification.permission as WebNotifPermission;
  }
  try {
    const r = await Notification.requestPermission();
    return r as WebNotifPermission;
  } catch {
    return "denied";
  }
}

export function hasSoftPromptedForWebNotifications(): boolean {
  if (typeof window === "undefined") return true;
  return window.localStorage.getItem(SOFT_PROMPT_KEY) === "1";
}

export function markSoftPromptedForWebNotifications(): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(SOFT_PROMPT_KEY, "1");
}

export interface ShowWebNotificationOpts {
  title: string;
  body?: string;
  /** Collapse repeated notifications from the same source (e.g. conversationId). */
  tag?: string;
  /** Invoked when the user clicks the OS notification. Called after focus. */
  onClick?: () => void;
}

/**
 * Show an OS-level notification. Silently returns if:
 *  - unsupported / native
 *  - permission not granted
 *  - the tab is currently focused (caller should already gate on this)
 */
export function showWebNotification(opts: ShowWebNotificationOpts): void {
  if (!isWebNotificationSupported()) return;
  if (Notification.permission !== "granted") return;
  if (typeof document !== "undefined" && document.hasFocus()) return;

  try {
    const n = new Notification(opts.title, {
      body: opts.body,
      tag: opts.tag,
      // renotify:true forces Windows/Chromium to re-alert on each notification
      // that shares a tag. Without it, the 2nd+ notif with the same tag
      // silently updates the existing popup — the "one popup ever" bug.
      // Requires `tag` to be set; browsers ignore renotify when tag is missing.
      renotify: opts.tag ? true : undefined,
      icon: "/favicon.png",
      badge: "/favicon.png",
      silent: !getSoundEnabled(),
    } as NotificationOptions);
    n.onclick = () => {
      try { window.focus(); } catch { /* ignore */ }
      try { opts.onClick?.(); } catch (e) { console.warn("web notif onClick failed", e); }
      try { n.close(); } catch { /* ignore */ }
    };
  } catch (e) {
    console.warn("showWebNotification failed", e);
  }
}
